import {
  ArrowDownOutlined,
  Box,
  Flex,
  FormGroup,
  Modal,
  useForm,
} from '@ergolabs/ui-kit';
import axios from 'axios';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { map, of, skip, switchMap } from 'rxjs';
import styled from 'styled-components';

import { balance$ } from '../../../../api/balance/balance';
import { getErgoSigUsdRate } from '../../../../api/ergoSigUsdRate/ergoSigUsdRate';
import { getWhitelistTokenBalance } from '../../../../api/getWhitelistTokenBalance/getWhitelistTokenBalance';
import { networkAssetBalance$ } from '../../../../api/networkAssetBalance/networkAssetBalance';
import { totalFees } from '../../../../api/submitContributionTx/buildTx';
import { ErgopayWallet } from '../../../../api/wallet/ergopay/ergopay';
import { selectedWallet$ } from '../../../../api/wallet/wallet';
import { applicationConfig } from '../../../../applicationConfig';
import { sigUsdAsset } from '../../../../common/assets/sigUsdAsset';
import { spfAsset } from '../../../../common/assets/spfAsset';
import { AssetControlFormItem } from '../../../../common/components/AssetControl/AssetControl';
import {
  openConfirmationModal,
  Operation,
} from '../../../../common/components/ConfirmationModal/ConfirmationModal';
import { ErgoPayModal } from '../../../../common/components/ErgoPayModal/ErgoPayModal';
import { FormTitle } from '../../../../common/components/FormTitle/FormTitle';
import {
  OperationForm,
  OperationValidator,
} from '../../../../common/components/OperationForm/OperationForm';
import { useIdoInfo } from '../../../../common/hooks/useIdoInfo';
import {
  useObservable,
  useSubscription,
} from '../../../../common/hooks/useObservable';
import { Currency } from '../../../../common/models/Currency';
import { Ratio } from '../../../../common/models/Ratio';
import { networkAsset } from '../../../../common/services/networkAsset';
import { ContributionConfirmationModal } from './ContributionConfirmationModal';
import { ContributionErgoPayModal } from './ContributionErgoPayModal';
import { ContributionFormModel } from './ContributionFormModel';

const ArrowContainer = styled(Flex.Item)`
  height: 4px;
  position: relative;
  z-index: 2;
`;

const ArrowBox = styled(Box)`
  background: #141414;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export interface ContributionFormProps {
  readonly whitelistTokenId: string;
  readonly proxyNft: string;
  readonly title?: ReactNode | ReactNode[] | string;
}

export const ContributionForm: FC<ContributionFormProps> = ({
  whitelistTokenId,
  proxyNft,
  title,
}) => {
  const { currentRound } = useIdoInfo();
  const [ratio, setRatio] = useState<Ratio>(currentRound?.tokenPrice as Ratio);
  const [selectedWallet] = useObservable(selectedWallet$);
  const [ergoRatio, setErgoRatio] = useState<number | undefined>();
  const [whitelistTokenBalance] = useObservable(
    getWhitelistTokenBalance(whitelistTokenId),
  );
  const [networkAssetBalance] = useObservable(networkAssetBalance$);
  const [balance] = useObservable(balance$);
  const form = useForm<ContributionFormModel>({
    from: undefined,
    fromAsset: sigUsdAsset,
    to: undefined,
    toAsset: spfAsset,
  });

  useEffect(() => {
    axios
      .get(`${applicationConfig.ergopadUrl}/asset/price/ergo`)
      .then((res) => setErgoRatio(res.data.price));
  }, []);

  const getMaxAmount = () => {
    if (!balance || !form.value.fromAsset) {
      return undefined;
    }
    if (form.value.fromAsset.id === networkAsset.id) {
      return balance.get(form.value.fromAsset).minus(totalFees).minus(100000n);
    }
    return balance.get(form.value.fromAsset);
  };

  useEffect(() => {
    if (!form.value.to?.isPositive() && whitelistTokenBalance) {
      // TODO: REPLACE WITH CONFIG VARIABLE
      form.patchValue({
        to: new Currency(whitelistTokenBalance.amount, spfAsset),
      });
    }
  }, [whitelistTokenBalance]);

  useSubscription(
    form.controls.to.valueChanges$,
    () => {
      if (currentRound && form.value.to) {
        let from = ratio.toBaseCurrency(form.value.to);

        if (from.asset.id === networkAsset.id) {
          from = from.percent(101);
        }

        form.patchValue(
          {
            from,
          },
          { emitEvent: 'silent' },
        );
      }
    },
    [ratio.baseAsset.id],
  );

  useSubscription(
    form.controls.from.valueChanges$.pipe(skip(1)),
    (from) => {
      if (currentRound) {
        let to: Currency | undefined;

        if (from?.asset.id === networkAsset.id) {
          to = from ? ratio.toQuoteCurrency(from.percent(100, 101)) : undefined;
        } else {
          to = from ? ratio.toQuoteCurrency(from) : undefined;
        }

        form.patchValue(
          {
            to,
          },
          { emitEvent: 'silent' },
        );
      }
    },
    [ratio.baseAsset.id],
  );

  useSubscription(
    form.controls.fromAsset.valueChangesWithSilent$.pipe(skip(1)).pipe(
      switchMap((fromAsset) =>
        fromAsset.id === sigUsdAsset.id
          ? of(currentRound?.tokenPrice)
          : getErgoSigUsdRate().pipe(
              map((ergoSigUsdRatio) =>
                currentRound?.tokenPrice.invertRatio().cross(ergoSigUsdRatio),
              ),
              map((ergoSigUsdRatio) => ergoSigUsdRatio?.invertRatio()),
            ),
      ),
    ),
    (ratio) => {
      if (ratio) {
        setRatio(ratio);
      }
    },
    [],
  );

  const whitelistTokenValidator: OperationValidator<
    ContributionFormModel
  > = () =>
    whitelistTokenBalance?.isPositive() ? undefined : 'No whitelist tokens';

  const enterAmountValidator: OperationValidator<ContributionFormModel> = (
    form,
  ) => (form.value.from?.isPositive() ? undefined : 'Enter an amount');

  const insufficientFundsValidator: OperationValidator<
    ContributionFormModel
  > = ({ value: { from, fromAsset } }) => {
    const fromBalance = balance?.get(fromAsset);
    const fromInput = from;

    if (!fromInput?.isPositive() || !fromBalance) {
      return undefined;
    }
    if (!fromInput?.isAssetEquals(fromBalance.asset)) {
      return undefined;
    }
    return fromBalance?.gte(fromInput)
      ? undefined
      : `Insufficient ${fromAsset.ticker} balance`;
  };

  const insufficientFundsForFeesValidator: OperationValidator<
    ContributionFormModel
  > = ({ value: { from } }) => {
    if (!networkAssetBalance) {
      return undefined;
    }
    if (
      from?.isAssetEquals(networkAsset) &&
      from?.isAssetEquals(networkAssetBalance.asset)
    ) {
      return networkAssetBalance.gte(from?.plus(totalFees) || totalFees)
        ? undefined
        : `Insufficient ${networkAsset.ticker} balance for fees`;
    }

    return networkAssetBalance.gte(totalFees)
      ? undefined
      : `Insufficient ${networkAsset.ticker} balance for fees`;
  };

  const action = ({ value }: FormGroup<ContributionFormModel>) => {
    if (
      !value.from?.isPositive() ||
      !value.to?.isPositive() ||
      !selectedWallet
    ) {
      return;
    }
    if (selectedWallet.name === ErgopayWallet.name) {
      Modal.open(({ close }) => (
        <ErgoPayModal
          openWalletContent={(onTxRegister) => (
            <ContributionErgoPayModal
              whitelistTokenId={whitelistTokenId}
              onClose={onTxRegister}
              value={value as Required<ContributionFormModel>}
              proxyNft={proxyNft}
            />
          )}
          onTxRegister={() => {}}
          close={close}
        />
      ));
    } else {
      openConfirmationModal(
        (next) => (
          <ContributionConfirmationModal
            whitelistTokenId={whitelistTokenId}
            onClose={next}
            value={value as Required<ContributionFormModel>}
            proxyNft={proxyNft}
          />
        ),
        Operation.CONTRIBUTION,
        {
          spfAsset: value.to,
          amountAsset: value.from,
        },
      );
    }
  };

  return (
    <Box borderRadius="l" padding={4}>
      <OperationForm
        validators={[
          whitelistTokenValidator,
          enterAmountValidator,
          insufficientFundsValidator,
          insufficientFundsForFeesValidator,
        ]}
        form={form}
        actionCaption="Get SPF"
        onSubmit={action}
      >
        <Flex col>
          <Flex.Item marginBottom={6}>
            <FormTitle
              content={`You may contribute the entire sum in ERG or SigUSD. If you wish to contribute ERG, it will be taken at an exchange of ~$${ergoRatio} ERG/SigUSD.`}
            >
              {title}
            </FormTitle>
          </Flex.Item>
          <AssetControlFormItem
            assets$={of([sigUsdAsset, networkAsset])}
            amountName="from"
            tokenName="fromAsset"
            showBalances
            getMaxAmount={getMaxAmount}
          />
          <ArrowContainer>
            <ArrowBox height={32} width={32} borderRadius="s">
              <Flex align="center" justify="center" stretch width="100%">
                <ArrowDownOutlined
                  style={{ color: 'var(--spectrum-primary-text)' }}
                />
              </Flex>
            </ArrowBox>
          </ArrowContainer>
          <AssetControlFormItem amountName="to" tokenName="toAsset" readonly />
        </Flex>
      </OperationForm>
    </Box>
  );
};
